import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { companySettingsApi, type InvoicingSettings as InvoicingSettingsType } from '../../api/settings'
import { includedServicesApi, type IncludedServiceItem } from '../../api/includedServices'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { Modal } from '../../components/ui/Modal'

const CURRENCIES = [
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'ALL', label: 'ALL — Albanian lek (L)' },
  { value: 'USD', label: 'USD — US dollar ($)' },
]

interface IncForm { id?: string; textEn: string; textIt: string; sortOrder: string; isActive: boolean }
const EMPTY_INC: IncForm = { textEn: '', textIt: '', sortOrder: '0', isActive: true }

export function InvoicingSettings() {
  const { companyId = '' } = useParams()
  const qc = useQueryClient()
  const { data: company } = useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: () => companySettingsApi.getGeneral(companyId).then(r => r.data),
  })
  const [form, setForm] = useState<InvoicingSettingsType>({})
  useEffect(() => {
    if (!company) return
    const inv = (company as any).settings?.invoicing ?? {}
    // Seed the IT box from the legacy single-language note so an existing company
    // sees its text straight away; saving then migrates it into footerNoteIt.
    setForm({ currency: 'EUR', ...inv, footerNoteIt: inv.footerNoteIt ?? inv.footerNote ?? '' })
  }, [company])

  const saveMut = useMutation({
    mutationFn: () => companySettingsApi.updateInvoicing(companyId, form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-settings', companyId] }),
  })

  const set = (k: keyof InvoicingSettingsType) => (v: string) => setForm(f => ({ ...f, [k]: v }))

  // ── Services included ──
  const [incForm, setIncForm] = useState<IncForm | null>(null)
  const { data: included = [] } = useQuery({
    queryKey: ['included-services', companyId, { includeInactive: true }],
    queryFn: () => includedServicesApi.list(companyId, { includeInactive: true }),
  })
  const invalidateInc = () => qc.invalidateQueries({ queryKey: ['included-services', companyId] })
  const saveInc = useMutation({
    mutationFn: (f: IncForm) =>
      f.id
        ? includedServicesApi.update(companyId, f.id, { textEn: f.textEn, textIt: f.textIt, sortOrder: Number(f.sortOrder) || 0, isActive: f.isActive })
        : includedServicesApi.create(companyId, { textEn: f.textEn || undefined, textIt: f.textIt || undefined, sortOrder: Number(f.sortOrder) || 0 }),
    onSuccess: () => { invalidateInc(); setIncForm(null) },
  })
  const delInc = useMutation({ mutationFn: (id: string) => includedServicesApi.remove(companyId, id), onSuccess: invalidateInc })
  const incLabel = (s: IncludedServiceItem) => s.textIt || s.textEn || '—'

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-semibold mb-1">Invoicing</h2>
      <p className="text-sm text-gray-500 mb-4">
        Clinic details printed on every proforma PDF. These belong to this company only.
      </p>

      <label className="label">Address</label>
      <input className="input mb-3" value={form.address ?? ''} onChange={e => set('address')(e.target.value)} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Phone</label>
          <input className="input mb-3" value={form.phone ?? ''} onChange={e => set('phone')(e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input mb-3" value={form.email ?? ''} onChange={e => set('email')(e.target.value)} />
        </div>
      </div>

      <label className="label">NIPT / Tax ID</label>
      <input className="input mb-3" value={form.taxId ?? ''} onChange={e => set('taxId')(e.target.value)} />

      <label className="label">Currency</label>
      <DropdownSelect
        className="mb-3"
        value={form.currency ?? 'EUR'}
        options={CURRENCIES}
        onChange={v => set('currency')(v)}
      />
      <p className="text-xs text-gray-400 mb-3 -mt-2">New proformas snapshot this currency; existing ones keep theirs.</p>

      <label className="label">Footer note (IT)</label>
      <textarea className="input mb-1" rows={4} value={form.footerNoteIt ?? ''}
        placeholder="Il preventivo include: (1) …"
        onChange={e => set('footerNoteIt')(e.target.value)} />
      <p className="text-xs text-gray-400 mb-3">
        Keep any closing legal sentence on its own line — selected services are appended
        to the last numbered line, so the closer stays last.
      </p>

      <label className="label">Footer note (EN)</label>
      <textarea className="input mb-1" rows={4} value={form.footerNoteEn ?? ''}
        placeholder="The quote includes: (1) …"
        onChange={e => set('footerNoteEn')(e.target.value)} />
      <p className="text-xs text-gray-400 mb-4">Falls back to the Italian note if left empty.</p>

      <label className="label">Brand colour</label>
      <div className="flex items-center gap-2 mb-1">
        <input
          type="color"
          className="h-9 w-12 rounded border border-gray-300 p-0.5 cursor-pointer"
          value={form.brandColor ?? '#334155'}
          onChange={e => set('brandColor')(e.target.value)}
        />
        <input
          className="input"
          style={{ maxWidth: 140 }}
          value={form.brandColor ?? ''}
          placeholder="#334155"
          onChange={e => set('brandColor')(e.target.value)}
        />
      </div>
      <p className="text-xs text-gray-400 mb-4">Themes the proforma PDF (banner, table header, total). Defaults to slate if empty.</p>

      <label className="label">Payment terms</label>
      <textarea className="input mb-4" rows={3} value={form.paymentTerms ?? ''}
        placeholder="e.g. Valid 30 days. 50% deposit confirms booking. IBAN AL47 …"
        onChange={e => set('paymentTerms')(e.target.value)} />

      <button className="btn btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
        {saveMut.isPending ? 'Saving…' : 'Save'}
      </button>
      {saveMut.isSuccess && <span className="ml-3 text-sm text-green-600">Saved.</span>}

      {/* SERVICES INCLUDED */}
      <div className="mt-10 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Services included</h2>
            <p className="text-sm text-gray-500">
              Optional lines appended to the footer note, numbered after its last item.
              Chosen per proforma. No price, not part of the service catalogue.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setIncForm(EMPTY_INC)}><Plus className="w-4 h-4" /> Add</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-2 w-12">#</th><th className="py-2">Italian</th><th className="py-2">English</th>
            <th className="py-2 text-center">Active</th><th className="py-2 w-20"></th>
          </tr></thead>
          <tbody>
            {included.map(s => (
              <tr key={s.id} className="border-b last:border-0">
                <td className="py-2 text-gray-400">{s.sortOrder}</td>
                <td className="py-2">{s.textIt || '—'}</td>
                <td className="py-2">{s.textEn || '—'}</td>
                <td className="py-2 text-center">{s.isActive ? '✓' : '—'}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button className="btn btn-ghost btn-sm" onClick={() => setIncForm({ id: s.id, textEn: s.textEn ?? '', textIt: s.textIt ?? '', sortOrder: String(s.sortOrder), isActive: s.isActive })}><Pencil className="w-4 h-4" /></button>
                  <button className="btn btn-ghost btn-sm text-red-600" onClick={() => { if (confirm(`Delete "${incLabel(s)}"?`)) delInc.mutate(s.id) }}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {!included.length && <tr><td colSpan={5} className="py-6 text-center text-gray-400">No lines yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!incForm} onClose={() => setIncForm(null)} title={incForm?.id ? 'Edit line' : 'Add line'} size="sm">
        {incForm && (
          <>
            <label className="label">Testo (IT)</label>
            <input className="input mb-3" value={incForm.textIt} onChange={e => setIncForm({ ...incForm, textIt: e.target.value })} />
            <label className="label">Text (EN)</label>
            <input className="input mb-3" value={incForm.textEn} onChange={e => setIncForm({ ...incForm, textEn: e.target.value })} />
            <label className="label">Order</label>
            <input className="input mb-3" type="number" min="0" step="1" value={incForm.sortOrder} onChange={e => setIncForm({ ...incForm, sortOrder: e.target.value })} />
            {incForm.id && (
              <label className="flex items-center gap-2 text-sm mb-3">
                <input type="checkbox" className="w-3.5 h-3.5" checked={incForm.isActive} onChange={e => setIncForm({ ...incForm, isActive: e.target.checked })} /> Active
              </label>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setIncForm(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm"
                disabled={(!incForm.textEn.trim() && !incForm.textIt.trim()) || saveInc.isPending}
                onClick={() => saveInc.mutate(incForm)}>{incForm.id ? 'Save' : 'Create'}</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
